import React from 'react';
import TrajectoryChart from './TrajectoryChart';
import { formatValue, formatRange } from './format';

// Is the projection heading toward or away from the target? Direction-aware,
// so "falling resting HR" reads as improving while "falling steps" doesn't.
export const assess = (m) => {
  const t = m.target || {};
  if (m.trend === 'flat' || m.projected_value == null || m.fit_now == null) {
    return { text: 'holding steady', tone: 'flat' };
  }
  const good = { text: 'improving', tone: 'good' };
  const bad = { text: 'drifting off target', tone: 'bad' };
  if (m.direction === 'up') return m.trend === 'rising' ? good : bad;
  if (m.direction === 'down') return m.trend === 'falling' ? good : bad;
  // range: closer to the band is better
  const dist = (v) => {
    if (t.ultimate_low != null && v < t.ultimate_low) return t.ultimate_low - v;
    if (t.ultimate_high != null && v > t.ultimate_high) return v - t.ultimate_high;
    return 0;
  };
  return dist(m.projected_value) <= dist(m.fit_now) ? good : bad;
};

export const isInBand = (m) => {
  const t = m.target || {};
  const v = t.baseline;
  if (v == null) return false;
  if (m.direction === 'up') return t.ultimate_low != null && v >= t.ultimate_low;
  if (m.direction === 'down') return t.ultimate_high != null && v <= t.ultimate_high;
  return (
    t.ultimate_low != null && t.ultimate_high != null &&
    v >= t.ultimate_low && v <= t.ultimate_high
  );
};

const toneChip = {
  good: 'bg-good/15 text-good-strong dark:text-good',
  bad: 'bg-off/15 text-off-strong dark:text-off',
  flat: 'bg-gray-100 text-gray-700 dark:bg-surface-dark-raised dark:text-gray-300',
};

const Readout = ({ label, value, unit, tone }) => (
  <div>
    <p className={`text-sm font-semibold uppercase tracking-wider mb-0.5 ${tone}`}>{label}</p>
    <p className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">
      {value == null ? '—' : formatValue(value, unit)}
    </p>
  </div>
);

// The full view of a single metric: trajectory chart plus the
// Now/Next/12-week/Target readouts. Formerly the heart of TwinPage; now rendered
// inline when a home Dial is expanded. `bare` drops the surrounding chrome
// (title row) for use inside a card that already has one. `part` lets a dial
// split the card: 'chart' renders only the trajectory chart (the always-visible
// glance), 'details' renders the readouts/summary (tucked into the dial's
// dropdown), 'all' is the original full card.
const MetricCard = ({ metric, bare = false, part = 'all' }) => {
  const t = metric.target || {};
  const verdict = assess(metric);
  const inBand = isInBand(metric);
  const targetText = formatRange(t.ultimate_low, t.ultimate_high, metric.unit);
  const showChart = part !== 'details';
  const showDetails = part !== 'chart';

  const body = (
    <>
      {!bare && showDetails && (
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-base font-semibold text-gray-900 dark:text-white">{metric.label}</p>
          <div className="flex items-center gap-1.5">
            <span className={`text-sm px-2 py-0.5 rounded-full ${toneChip[verdict.tone]}`}>
              {verdict.text}
            </span>
          </div>
        </div>
      )}

      {showDetails && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          On your current path,{' '}
          <span className="font-semibold text-solar">
            {formatValue(metric.fit_now, metric.unit)}
          </span>{' '}
          today →{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {formatValue(metric.projected_value, metric.unit)}
          </span>{' '}
          in {metric.horizon_weeks} weeks.
        </p>
      )}

      {showChart && <TrajectoryChart metric={metric} />}

      {/* Left column = where you are / where you're drifting (neutral gray);
          right column = the goal-ward numbers (green). Same colour, same side —
          Now/Next on top, 12-wk path/Target below. 2-up on phone, 4-up on wide. */}
      {showDetails && (
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
        <Readout
          label="Now"
          value={metric.fit_now}
          unit={metric.unit}
          tone="text-gray-600 dark:text-gray-300"
        />
        {t.next_value != null && !inBand ? (
          <Readout
            label={t.next_horizon_weeks ? `Next · ${t.next_horizon_weeks}w` : 'Next'}
            value={t.next_value}
            unit={metric.unit}
            tone="text-good-strong dark:text-good"
          />
        ) : (
          <div aria-hidden />
        )}
        <Readout
          label="In 12 wks"
          value={metric.projected_value}
          unit={metric.unit}
          tone="text-gray-600 dark:text-gray-300"
        />
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-good-strong dark:text-good mb-0.5">
            Target
          </p>
          <p className="text-base font-bold text-gray-900 dark:text-white break-words">
            {targetText || '—'}
          </p>
        </div>
      </div>
      )}

      {showDetails && inBand && (
        <p className="mt-2 text-sm text-green-700 dark:text-green-400 font-medium">
          Already in your target range — the projection shows whether you hold it.
        </p>
      )}
    </>
  );

  return body;
};

export default MetricCard;
export { toneChip };
